<template>
  <div>
    <div class="w-100 d-flex justify-content-between mb-3 filters">
      <TableTitle icon="archive-outline" title="Pickups" />
      <SearchField @search-triggered="updateSearch" />
    </div>
    <easy-data-table
      ref="dataTable"
      v-model:server-options="serverOptions"
      table-class-name="vue3-easy-data-table table-shadow pickups"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :headers="headers"
      :items="items"
      :prevent-context-menu-row="false"
      :fixed-header="false"
      no-hover
      alternating
      hide-footer
      @update:server-options="updateServerOptions"
    >
      <template #loading>
        <b-spinner class="mt-5" variant="success" />
      </template>
      <template #no-data>
        <div style="text-align: center">No data available</div>
      </template>
      <template #item-user="{ company }: Pickup">
        {{ company.shortName || company.name }}
      </template>
      <template #item-senderAddress="{ senderAddress, instructions }: Pickup">
        <div>
          {{ toAddressString(senderAddress) }}
        </div>
        <div v-if="instructions">
          <small class="text-muted">{{ instructions }}</small>
        </div>
      </template>
      <template #item-assignment="{ assignments }: Pickup">
        <div v-for="assignment in assignments" :key="assignment.id">
          <router-link :to="useDetailsRoute(assignment)" class="pointer text-light-green">
            {{ assignment.reference }}
          </router-link>
        </div>
      </template>
      <template #item-carrier="{ carrier }: Pickup">
        <CarrierRowWithIcon :carrier="carrier" />
      </template>
      <template #item-pickupDate="{ pickupDate }: Pickup">
        <RowDate :date="pickupDate" :display-time="false" />
      </template>
      <template #item-createdAt="{ createdAt }: Pickup">
        <RowDate :date="createdAt" />
      </template>
      <template #item-identifier="{ identifier, error }: Pickup">
        <div v-if="identifier">
          {{ identifier }}
        </div>
        <div v-if="error">
          <small class="text-danger">
            {{ error }}
          </small>
        </div>
      </template>
      <template #item-status="{ status }: Pickup">
        {{ ucFirst(status) }}
      </template>
    </easy-data-table>

    <TablePagination
      v-model:server-options-ref="serverOptions"
      v-model:data-table-ref="dataTable"
      :server-items-length="serverItemsLength"
    />
  </div>
</template>

<script lang="ts">
import type { Pickup } from '@/types/Models/Pickup'
import { BSpinner } from 'bootstrap-vue-next'
import { defineComponent, ref, watch, type Ref, onMounted, computed, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'
import { useAddressTransformers } from '@/composables/Address/useAddressTransformers'
import { useDetailsRoute } from '@/composables/Assignment/useDetailsRoute'
import { ucFirst } from '@/services/Helpers'
import { usePickupStore } from '@/store/pickup'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import RowDate from '@/views/Components/Elements/Table/RowDate.vue'
import TableTitle from '@/views/Components/Elements/Table/TableTitle.vue'
import SearchField from '@/views/Components/PageParts/Search.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'
import CarrierRowWithIcon from '@/views/Components/Assignment/CarrierRowWithIcon.vue'

export default defineComponent({
  name: 'PickupsTable',
  components: {
    BSpinner,
    EasyDataTable,
    CarrierRowWithIcon,
    TablePagination,
    TableTitle,
    RowDate,
    SearchField,
  },
  setup() {
    const route = useRoute()
    const pickupStore = usePickupStore()

    const dataTable = ref(null)
    const search: Ref<string> = ref('')
    const items: ComputedRef<Pickup[]> = computed(() => pickupStore.pickupsList)
    const loading: ComputedRef<boolean> = computed(() => pickupStore.loading)
    const serverItemsLength: ComputedRef<number> = computed(() => pickupStore.pickupsCount)
    const serverOptions: Ref<ServerOptions> = ref({
      page: 1,
      rowsPerPage: 25,
    })
    const headers: Header[] = [
      { text: 'User', value: 'user' },
      { text: 'Address, instructions', value: 'senderAddress' },
      { text: 'Assignment', value: 'assignment' },
      { text: 'Status', value: 'status' },
      { text: 'Carrier', value: 'carrier' },
      { text: 'Identifier, error', value: 'identifier' },
      { text: 'Pickup date', value: 'pickupDate' },
      { text: 'Created at', value: 'createdAt' },
    ]

    const loadFromServer = async () => {
      const params = {
        page: serverOptions.value.page,
        limit: serverOptions.value.rowsPerPage,
        ascending: 1,
        byColumn: 0,
        query: search.value,
      }

      await pickupStore.fetch({ ...params, ...route.query })
    }

    const { toAddressString } = useAddressTransformers()

    const updateServerOptions = (newOptions: ServerOptions) => {
      serverOptions.value = newOptions
    }

    const updateSearch = (value: string) => {
      search.value = value
    }

    onMounted(loadFromServer)

    watch(serverOptions, loadFromServer, { deep: true })
    watch(search, loadFromServer, { deep: true })

    return {
      items,
      loading,
      headers,
      dataTable,
      serverOptions,
      serverItemsLength,
      updateServerOptions,
      toAddressString,
      useDetailsRoute,
      updateSearch,
      ucFirst,
    }
  },
})
</script>
